// The abstract `chrome-extension.full-page-dialog` state and the screens under it (plan §7
// phase 5) — what the extension opens as a full-page iframe OVER the ATS page rather than in
// the sidebar.
//
// ⚠️ THEY ARE NOT EXTENSION-TAB SCREENS, AND THEY ARE NOT AUTH SCREENS. Three of the four
// layouts in this app now: `AuthLayout` renders the auth cascade's host chain, `ExtensionLayout`
// renders the tab chrome, and this renders an overlay and a fixed-width column. A dialog routed
// under either of the others would inherit chrome the reference captures do not have.
//
// ⚠️ AND MOST OF THEM ARE UNGUARDED, WHICH IS UPSTREAM'S OWN SHAPE. Only `/email-signin`
// declares `privAccess`; `pending`, `redirect`, `host-redirect` and `deletealert` do not,
// because they are opened from an email link or mid-handoff, when there may be no session at
// all. Their reference captures come from production SIGNED OUT and render fully — so a guard
// here would redirect screens that are meant to work without one.
import { useEffect, useState, type ReactElement } from 'react'
import { Outlet, useLocation, useSearchParams } from 'react-router-dom'
import { fetchViewModel } from '@woo-ext/api'
import { routes } from '@woo-ext/protocol'
import {
  DeleteAlertPage,
  FullPageDialog,
  getAtsLabel,
  PendingPage,
  RedirectPage,
  RegisterPromotionPage,
  UninstallPage,
} from '@woo-ext/ui'
import { ReportsRoute } from './ReportsRoute.tsx'
import { extensionTarget } from '../extension-target.ts'
import { useRouteChrome } from './useRouteChrome.ts'
import { useDialogFrame } from './useCloseDialog.ts'

/** The ui-router state name for a path, which is what the frame's class is built from. */
export function stateNameFor(pathname: string): string | undefined {
  return routes.find((route) => route.path === pathname && !route.abstract)?.state
}

export function FullPageDialogLayout(): ReactElement {
  const { pathname } = useLocation()
  // `bodyClass: 'no-min-width'` on all but `deletealert` and `myaccount`, and it is inherited
  // per key exactly as everywhere else — see `routeData.ts`.
  useRouteChrome()
  // Announces `Ready` to the parent on mount and hands back the overlay's close — the two
  // messages `full-page-dialog.component.ng1.ts` sends. `undefined` outside a frame, which
  // is every reference capture, so the overlay stays inert exactly where it was before.
  const onClose = useDialogFrame()
  return (
    <FullPageDialog
      {...(stateNameFor(pathname) === undefined ? {} : { stateName: stateNameFor(pathname) })}
      {...(onClose === undefined ? {} : { onClose })}
    >
      <Outlet />
    </FullPageDialog>
  )
}

/** `chrome-extension.full-page-dialog.redirect` — `?rd&params`, resolved by the shell. */
function RedirectRoute(): ReactElement {
  return <RedirectPage />
}

/** `chrome-extension.full-page-dialog.host-redirect` — `?type&params`. */
function HostRedirectRoute(): ReactElement {
  const [params] = useSearchParams()
  const type = params.get('type')
  // `ctrl.atsLabel` is `getAtsLabel($window)` upstream — the bootstrap, which this app fetches
  // rather than having inlined. It is read only in the success message, and a tenant with no ATS
  // name renders the sentence without one, which is what the reference capture holds.
  const [atsLabel, setAtsLabel] = useState<string | undefined>(undefined)
  useEffect(() => {
    let live = true
    fetchViewModel().then(
      (model) => {
        if (live) setAtsLabel(getAtsLabel(model))
      },
      () => undefined,
    )
    return () => {
      live = false
    }
  }, [])
  return (
    <RedirectPage
      host
      {...(type === null ? {} : { redirectType: type })}
      {...(atsLabel === undefined ? {} : { atsLabel })}
    />
  )
}

/** `chrome-extension.full-page-dialog.deletealert` — `?token`, and the token is never read. */
function DeleteAlertRoute(): ReactElement {
  const [params] = useSearchParams()
  // Read only to make the shape explicit: upstream's controller takes `$stateParams` and its
  // `deleteAlert` is an empty deprecated method, so the token reaches nothing on either side.
  void params.get('token')
  return <DeleteAlertPage />
}

/**
 * `chrome-extension.full-page-dialog.register-promotion` — `?type`, which selects from a
 * one-entry record and falls back to that entry for anything else.
 */
function RegisterPromotionRoute(): ReactElement {
  const [storeUrl, setStoreUrl] = useState<string | undefined>(undefined)
  useEffect(() => {
    let live = true
    fetchViewModel().then(
      (model) => {
        if (!live) return
        // ⚠️ RESOLVED, NOT READ STRAIGHT OFF THE BOOTSTRAP. `careero.extensionId` is the LEGACY
        // item's id (CLAUDE.md 6), and this link is what the visitor installs — so it goes
        // through `extensionTarget`, which prefers the id this build knows about and falls back
        // to the tenant's only when there is none.
        setStoreUrl(`${STORE_URL_PREFIX}${extensionTarget(model).id}`)
      },
      () => undefined,
    )
    return () => {
      live = false
    }
  }, [])
  return <RegisterPromotionPage {...(storeUrl === undefined ? {} : { storeUrl })} />
}

/** `registerUrlTemplate` — the Chrome Web Store item, with the extension id appended. */
const STORE_URL_PREFIX = 'https://chromewebstore.google.com/detail/smart-search-by-woo/'

export const dialogRoutes = [
  { path: '/chrome-extension/pending', element: <PendingPage /> },
  { path: '/chrome-extension/redirect', element: <RedirectRoute /> },
  { path: '/chrome-extension/host-redirect', element: <HostRedirectRoute /> },
  { path: '/chrome-extension/deletealert', element: <DeleteAlertRoute /> },
  { path: '/chrome-extension/register-promotion', element: <RegisterPromotionRoute /> },
  { path: '/chrome-extension/uninstall', element: <UninstallPage /> },
  // ⚠️ GUARDED UPSTREAM ONLY BY A `TODO`. `reports.state.config.ng1.ts` carries
  // `// TODO avif: restrict to logged in users (if not already restricted)` and declares no
  // `privAccess`, so the state itself lets a signed-out visitor in and the ENDPOINTS are what
  // refuse. Reproduced rather than tightened: a guard here would redirect a screen whose
  // reference was captured without one, and the permission it really needs is an account
  // grant on the tenant, not a client-side check.
  { path: '/chrome-extension/reports', element: <ReportsRoute /> },
]
